import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import DealerLayout from '../components/DealerLayout';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { C } from '../theme';

const REASON_CFG = {
  damaged:     { label: 'Damaged',     bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B' },
  expired:     { label: 'Expired',     bg: '#FEF3C7', color: '#B45309', dot: '#D97706' },
  not_working: { label: 'Not Working', bg: '#FFE4E6', color: '#BE123C', dot: '#FB7185' },
  lost:        { label: 'Lost',        bg: '#FEE2E2', color: '#991B1B', dot: '#EF4444' },
  other:       { label: 'Other',       bg: '#F3F4F6', color: '#374151', dot: '#9CA3AF' },
};

const SOURCE_CFG = {
  renewal: { label: 'Renewal',  bg: '#FEF3C7', color: '#92400E' },
  restore: { label: 'Restore',  bg: '#D1FAE5', color: '#065F46' },
};

export default function ReplacementHistory() {
  const { user } = useAuth();
  const isDealer = user?.role === 'dealer';
  const LayoutComp = isDealer ? DealerLayout : Layout;
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [reason,  setReason]  = useState('all');
  const [source,  setSource]  = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/replacement-history');
      setItems(r.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter(it => {
      if (reason !== 'all' && it.reason !== reason) return false;
      if (source !== 'all' && it.source !== source) return false;
      if (!q) return true;
      return [
        it.client_name, it.client_ref, it.mapping_id,
        it.old_item_id, it.old_serial,
        it.new_item_id, it.new_serial,
        it.category_name, it.type_name,
        it.installer_name,
      ].some(v => v?.toLowerCase().includes(q));
    });
  }, [items, search, reason, source]);

  const summary = useMemo(() => {
    const total   = items.length;
    const byReason = {};
    const bySource = { renewal: 0, restore: 0 };
    items.forEach(it => {
      byReason[it.reason] = (byReason[it.reason] || 0) + 1;
      if (it.source) bySource[it.source] = (bySource[it.source] || 0) + 1;
    });
    return { total, byReason, bySource };
  }, [items]);

  return (
    <LayoutComp title="Replacement History">

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '22px' }}>
        <SummCard icon="🔄" label="Total Replacements" value={summary.total}              bg="#EDE9FE" color="#5B21B6" dot="#7C3AED" />
        <SummCard icon="🔁" label="Via Renewal"         value={summary.bySource.renewal || 0} bg="#FEF3C7" color="#92400E" dot="#F59E0B" />
        <SummCard icon="↩"  label="Via Restore"         value={summary.bySource.restore || 0} bg="#D1FAE5" color="#065F46" dot="#10B981" />
        <SummCard icon="💥" label="Damaged"             value={summary.byReason.damaged     || 0} bg="#FEF3C7" color="#92400E" dot="#F59E0B" />
        <SummCard icon="📴" label="Not Working"         value={summary.byReason.not_working || 0} bg="#FFE4E6" color="#BE123C" dot="#FB7185" />
        <SummCard icon="🔴" label="Lost"                value={summary.byReason.lost        || 0} bg="#FEE2E2" color="#991B1B" dot="#EF4444" />
      </div>

      {/* Table card */}
      <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>

        {/* Filters */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            placeholder="Search client, serial, item ID, category…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '8px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ padding: '8px 12px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
              Clear
            </button>
          )}

          {/* Source filter */}
          <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', borderRadius: '8px', padding: '3px' }}>
            {[
              { key: 'all',     label: 'All Sources' },
              { key: 'renewal', label: '🔁 Renewal'  },
              { key: 'restore', label: '↩ Restore'   },
            ].map(opt => (
              <button key={opt.key} onClick={() => setSource(opt.key)} style={{
                padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 700,
                border: 'none', cursor: 'pointer',
                background: source === opt.key ? C.white : 'transparent',
                color:      source === opt.key ? C.textMain : C.textSub,
                boxShadow:  source === opt.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Reason filter */}
          <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', borderRadius: '8px', padding: '3px' }}>
            {[{ key: 'all', label: 'All Reasons' }, ...Object.entries(REASON_CFG).map(([k, v]) => ({ key: k, label: v.label }))].map(opt => (
              <button key={opt.key} onClick={() => setReason(opt.key)} style={{
                padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 700,
                border: 'none', cursor: 'pointer',
                background: reason === opt.key ? (REASON_CFG[opt.key]?.bg || C.white) : 'transparent',
                color:      reason === opt.key ? (REASON_CFG[opt.key]?.color || C.textMain) : C.textSub,
                boxShadow:  reason === opt.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>
                {opt.label}
              </button>
            ))}
          </div>

          <button onClick={load} title="Refresh" style={{ padding: '8px 12px', background: C.primaryBg, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>↻</button>
          <span style={{ fontSize: '12px', color: C.textSub, whiteSpace: 'nowrap' }}>{filtered.length} items</span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '980px' }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {['Date', 'Source', 'Client', 'Mapping', 'Category / Type', 'Old Unit', 'New Unit', 'Reason', 'Cost Δ', 'Installer'].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', fontSize: '11px', fontWeight: 700,
                    color: C.textSub, textAlign: 'left', textTransform: 'uppercase',
                    letterSpacing: '0.5px', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '70px', color: C.textSub, fontSize: '13px' }}>Loading replacement history…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '70px' }}>
                  <div style={{ fontSize: '40px', marginBottom: '14px' }}>🔄</div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: C.textMain, marginBottom: '5px' }}>No replacements found</div>
                  <div style={{ fontSize: '12px', color: C.textSub }}>
                    {items.length === 0
                      ? 'Replacements are recorded when you swap an IMEI during Renewal or Restore.'
                      : 'No items match the current filter.'}
                  </div>
                </td></tr>
              ) : (
                filtered.map((it, i) => {
                  const rcfg = REASON_CFG[it.reason] || REASON_CFG.other;
                  const scfg = SOURCE_CFG[it.source] || { label: it.source, bg: '#F3F4F6', color: '#374151' };
                  const costDelta = (it.new_amount || 0) - (it.old_amount || 0);

                  return (
                    <tr key={it.id} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA', borderBottom: `1px solid ${C.border}` }}>

                      {/* Date */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: C.textMain }}>{it.replaced_at?.split(' ')[0] || '—'}</div>
                        <div style={{ fontSize: '11px', color: C.textSub }}>{it.replaced_at?.split(' ')[1] || ''}</div>
                      </td>

                      {/* Source */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: scfg.bg, color: scfg.color, whiteSpace: 'nowrap' }}>
                          {scfg.label}
                        </span>
                      </td>

                      {/* Client */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: C.textMain }}>{it.client_name}</div>
                        <div style={{ fontSize: '10px', color: C.textSub, fontFamily: 'monospace' }}>{it.client_ref}</div>
                      </td>

                      {/* Mapping */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#7C3AED' }}>
                          {it.mapping_id || '—'}
                        </span>
                      </td>

                      {/* Category / Type */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: C.textMain }}>{it.category_name}</div>
                        {it.type_name && (
                          <span style={{ fontSize: '11px', background: C.primaryBg, color: C.primaryDeep, padding: '1px 8px', borderRadius: '10px', fontWeight: 600, display: 'inline-block', marginTop: '2px' }}>
                            {it.type_name}
                          </span>
                        )}
                      </td>

                      {/* Old unit */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        {it.old_item_id ? (
                          <>
                            <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: '#374151' }}>{it.old_item_id}</div>
                            {it.old_serial && <div style={{ fontSize: '11px', color: C.textSub }}>SN: <span style={{ fontFamily: 'monospace', color: '#6B7280' }}>{it.old_serial}</span></div>}
                            {it.old_amount > 0 && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>₹{it.old_amount.toLocaleString('en-IN')}</div>}
                          </>
                        ) : <span style={{ color: '#D1D5DB', fontSize: '12px' }}>—</span>}
                      </td>

                      {/* New unit */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        {it.new_item_id ? (
                          <>
                            <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#059669' }}>{it.new_item_id}</div>
                            {it.new_serial && <div style={{ fontSize: '11px', color: C.textSub }}>SN: <span style={{ fontFamily: 'monospace', color: '#374151', fontWeight: 600 }}>{it.new_serial}</span></div>}
                            {it.new_amount > 0 && <div style={{ fontSize: '11px', color: '#6B7280' }}>₹{it.new_amount.toLocaleString('en-IN')}</div>}
                          </>
                        ) : <span style={{ color: '#D1D5DB', fontSize: '12px' }}>—</span>}
                      </td>

                      {/* Reason */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: rcfg.bg, color: rcfg.color, padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: rcfg.dot, flexShrink: 0 }} />
                          {rcfg.label}
                        </span>
                      </td>

                      {/* Cost delta */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {costDelta !== 0 ? (
                          <span style={{ fontSize: '13px', fontWeight: 800, color: costDelta > 0 ? '#D97706' : '#059669' }}>
                            {costDelta > 0 ? '+' : ''}₹{costDelta.toLocaleString('en-IN')}
                          </span>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#D1D5DB' }}>—</span>
                        )}
                      </td>

                      {/* Installer */}
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
                        <div style={{ fontSize: '12px', color: C.textMain }}>{it.installer_name || '—'}</div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap', background: '#FAFAFA' }}>
            <span style={{ fontSize: '12px', color: C.textSub, fontWeight: 600 }}>SHOWING {filtered.length} REPLACEMENTS</span>
            {filtered.some(it => it.new_amount > 0) && (
              <>
                <span style={{ fontSize: '12px', color: C.textSub }}>|</span>
                <span style={{ fontSize: '12px', color: C.textSub }}>Total replacement cost:</span>
                <span style={{ fontSize: '14px', fontWeight: 800, color: '#D97706' }}>
                  ₹{filtered.reduce((s, it) => s + (it.new_amount || 0), 0).toLocaleString('en-IN')}
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </LayoutComp>
  );
}

function SummCard({ icon, label, value, bg, color, dot }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '16px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ fontSize: '20px' }}>{icon}</div>
        <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: dot, marginTop: '4px' }} />
      </div>
      <div style={{ fontSize: '26px', fontWeight: 900, color: C.textMain, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', fontWeight: 700, color, background: bg, display: 'inline-block', padding: '2px 8px', borderRadius: '6px', margin: '6px 0 0' }}>{label}</div>
    </div>
  );
}
